import { Injectable } from '@angular/core';
import {
    AccountMappingTemplateGroupItem,
    IAccountFilter,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IExtraExcelContent,
    IFinancialStatementImportDefinition,
    IFinancialStatementInterpretation,
    IItemNameType,
    ItemType,
    NumberFormats,
    Signs,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IAccountMappingMap } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IAccountMappingMap.interface';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { DefaultGroupsNames, TemplateType } from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
// REV-18627: konkreter Pfad statt Barrel — zirkulaerer Import in Node
import { ISheet } from '../interfaces/ISheet';
import { AUMICO_IMPORT } from '../constants/aumico-import';
import { COLUMN_NAME_IDENTIFICATION } from '../constants/column-name-identification';
import { MASTER_DATA_DEFAULT_COLUMN_NAMES } from '../constants/master-data-default-column-names';
import { MASTER_DATA_DEFAULT_SHEET_NAMES } from '../constants/master-data-default-sheet-names';
import { PartnerExcelImportFileType } from '../enums/partner-excel-import-file-type.enum';
import { ISheetRow } from '../interfaces/ISheetRow';
import { ISpreadSheet } from '../interfaces/ISpreadSheet';
import { SortingItemsHelper } from './sorting-items.helper';

const debug = Debug('aumico:annualreport-fe:DatevExcelHelper');

export interface ExcelHelper {
    isKnownFileFormat(spreadSheetData: ISpreadSheet): PartnerExcelImportFileType;
    getImportSettings(): ExcelImportDefinitions;
    createTaxonomy(
        spreadSheetData: ISpreadSheet,
        canUseUncovershorfall: boolean,
        canUseEquity: boolean,
    ): TaxonomyDefinitionsWithErrorInformation;
    extractExtraExcelContent(spreadSheetData: ISpreadSheet): IExtraExcelContent | null;
}

export enum DATEV_ERROR {
    MALFORMATED_FILE = 'aumico_annual_report_datev_error_malformatted_file',
    WRONG_ASSETS_DATA = 'aumico_annual_report_datev_error_wrong_asset_data',
    WRONG_LIBILTIE_DATA = 'aumico_annual_report_datev_error_wrong_liabilties_data',
    WRONG_INCOME_DATA = 'aumico_annual_report_datev_error_wrong_income_data',
    ROOT_NODE_EMPTY = 'aumico_annual_report_datev_error_root_node_empty',
    MISSING_TEMPLATE_INFO = 'aumico_annual_report_datev_error_missing_template_info',
    MISSING_TEMPLATE_STRUCTURE = 'aumico_annual_report_datev_error_missing_template_structure',
    // REV-18627 (I2): eine Postenstruktur-Zeile hat weder einen exakten noch einen zuletzt
    // gesehenen Vorfahren gefunden (kann nur ganz am Dateianfang passieren).
    ORPHAN_NODE = 'aumico_annual_report_datev_error_orphan_node',
}

export interface ImprovedAccountMappingTemplate {
    name: string;
    group: ImprovedMappingGroup;
    type: TemplateType;
    accountBasis: string;
    legalForm: string;
    profitOfTheYear: string;
    equityGroupName: string | null;
    uncoveredShortfallAssetsFli?: string;
    uncoveredShortfallLiabilitiesFli?: string;
    annualSurplusFli?: string;
    accountAssignments?: IAccountMappingMap;
    flis?: INameId[];
    groups?: INameId[];
}

export type SortedItem = AccountMappingTemplateGroupItem | ImprovedMappingGroup;
export interface ImprovedMappingGroup {
    name: string;
    title: string;
    totalName: string;
    hideTotal: boolean;
    groups: ImprovedMappingGroup[];
    items: AccountMappingTemplateGroupItem[];
    calculatedOpeningBalance?: number;
    calculatedClosingBalance?: number;
    header?: string;
    isEmpty?: boolean;
    isHeader?: boolean;
    isHiddenGroup?: boolean;
    sortedItems: SortedItem[];
}

interface DatevRows {
    evaluation: string;
    balanceType: string;
    level: number;
    itemText: string;
    mainCol: number;
    preCol: number;
    ofThatCol: number;
    accountAssignments: string[];
}
export interface SpecialFLIs {
    equityGroupName?: string;
    equityGroupNameId?: string;
    annualSurplus?: string;
    annualSurplusId?: string;
    profitOfTheYear?: string;
    profitOfTheYearId?: string;
    uncoverShortFallAssets?: string;
    uncoverShortFallAssetsId?: string;
    uncoverShortFallLiabilities?: string;
    uncoverShortFallLiabilitiesId?: string;
    cashFlow?: string;
    cashFlowId?: string;
}

export interface ExcelImportDefinitions {
    balanceSheet: IFinancialStatementImportDefinition;
    incomeStatement: IFinancialStatementImportDefinition;
    normalization: IFinancialStatementInterpretation;
    filter: IAccountFilter;
}
export interface TaxonomyDefinitions {
    rawBalanceTemplate: IAccountMappingTemplate;
    rawIncomeTemplate: IAccountMappingTemplate;
}
export interface TaxonomyDefinitionsWithErrorInformation {
    taxonomyDefinitions: TaxonomyDefinitions;
    errors: DATEV_ERROR[];
}

export enum NodeType {
    GROUP = 'GROUP',
    HIDDEN_GROUP = 'HIDDEN_GROUP',
    FLI = 'FLI',
    FLI_WITHOUT_NUMBERING = 'FLI_WITHOUT_NUMBERING',
    SUBFLI = 'SUBFLI',
    SUM = 'SUM',
    OF_THAT_ITEM = 'OF-THAT-ITEM',
    IGNORE = 'IGNORE',
}

interface TreeNode {
    id: string;
    data: DatevRows;
    children: TreeNode[];
    type?: string; // Optional type to distinguish between SUM and OF-THAT-ITEM leaves
    linked?: TreeNode; // Optional link if this is connected to another TreeNode (eg a SUM)
}

export interface AumicoNode {
    evaluation: string; // Corresponds to row.cells[0]
    level: number; // The level extracted or calculated
    classification: string; // Corresponds to row.cells[2]
    itemText: string; // Corresponds to row.cells[3]
    accountAssignments: string[]; // An array of trimmed account assignments from row.cells[4]
    id: string; // Corresponds to row.cells[5]
    disableNumbering: boolean; // to determine if the numbering should be disabled
    children: AumicoNode[]; // children nodes
}
export enum AumicoNodeType {
    GROUP = 'GROUP',
    HIDDEN_GROUP = 'HIDDEN_GROUP',
    FLI = 'FLI',
    FLI_WITHOUT_NUMBERING = 'FLI_WITHOUT_NUMBERING',
    X_FLI = 'X_FLI',
    REMOVE = 'REMOVE',
    TITLE = 'TITLE',
    SUB_FLI = 'SUB_FLI',
    SUM = 'SUM',
    OF_THAT_ITEM = 'OF_THAT_ITEM',
    UNKNOWN = 'UNKNOWN',
}

const ITEMS_TO_INGORE = ['Gezeichnetes Kapital in DM', 'Gezeichnetes Kapital in Euro', 'nicht gedeckter Fehlbetrag'];

/**
 * REV-22122: item name → DATEV `Saldoart` ("Aktiva" / "Passiva" / "Aufwand" / "Ertrag"), carried on
 * the imported template next to `accountAssignmentsFormat` (not part of the types package — the
 * template lives in `IAnnualReport.importedTaxonomy`, stored as a whole). Read by the mapping.
 */
export interface DatevTemplateSides {
    accountItemSides?: Record<string, string>;
}

const DATEV_ITEM_SIDES = ['Aktiva', 'Passiva', 'Aufwand', 'Ertrag'];

function collectItemSides(nodes: TreeNode[], sides: Record<string, string> = {}): Record<string, string> {
    nodes.forEach((node) => {
        // SRR F3: only the four DATEV sides travel with the template.
        const side = String(node?.data?.balanceType ?? '');
        if (DATEV_ITEM_SIDES.includes(side)) sides[node.id] = side;
        collectItemSides(node?.children ?? [], sides);
    });
    return sides;
}

/** REV-22122: the special items `createTree` also finds by the narrower `Posteninfo` readings. */
const TOLERANT_SPECIAL_ITEMS: string[] = ['profitOfTheYear', 'annualSurplus'];

/** REV-22122: true when a row assigns at least one real key — the `0-S-0` style placeholder does not count. */
function hasRealAssignment(assignments: string[]): boolean {
    return assignments.some((value) => !/^0\D+0$/.test(String(value).trim()));
}

/**
 * REV-22122: the item name a `Posteninfo` entry means. The account-number export names the item
 * exactly ("Bilanzgewinn Bilanzverlust"); the purpose export describes both outcomes of one item
 * ("Bilanzgewinn / Bilanzverlust") while its `Postenstruktur` names only the first
 * ("Bilanzgewinn"), and may prefix a context ("Eigenkapital Kapitalgesellschaft - Nicht durch
 * Eigenkapital gedeckter Fehlbetrag"). The exact name comes first; only when no item carries it
 * are the narrower readings tried, so a file that names its items exactly behaves as before.
 */
export function specialItemNameCandidates(name: string | undefined): string[] {
    const exact = (name ?? '').trim();
    if (exact === '') return [];
    const candidates = [exact];
    const beforeSlash = exact.split(' / ')[0].trim();
    if (beforeSlash !== exact) candidates.push(beforeSlash);
    const afterDash = exact.split(' - ').pop()!.trim();
    if (afterDash !== exact) candidates.push(afterDash);
    return candidates;
}

const BALANCE_TYPE_ASSETS = 'Aktiva';
const BALANCE_TYPE_LIABILITIES = 'Passiva';

const PREFIX_BALANCE_ASSETS_ITEM = 'ba_';
const PREFIX_BALANCE_LIABILITIES_ITEM = 'bp_';
const PREFIX_INCOME_ITEM = 'is_';

// --- REV-18627 (C1) Contract-Stubs: Signaturen, die der rote Contract festnagelt. ---------------

/** Zuordnungsformat einer DATEV-Datei (seams §5.1: String-Union, kein Enum). */
export type DatevAssignmentFormat = 'accountNumber' | 'purpose';

/** Dritter Literalwert von checkTaxonomyFormat (heute nur 'DATEV' | 'aumico' | 'Unknown'). */
export const DATEV_TAXONOMY_FORMAT_PURPOSE = 'DATEV_PURPOSE';

/**
 * Ergebnis der Formaterkennung. Der Spaltenkopf entscheidet (Postenstruktur!H1 bzw. Bilanz/GuV!C1);
 * widerspricht das Blatt Infodaten, gewinnt der Spaltenkopf und der Widerspruch wird gemeldet,
 * statt nur geloggt zu werden (seams N4: keine Rohinhalte in der Meldung).
 */
export interface DatevFormatDetection {
    fileType: PartnerExcelImportFileType;
    assignmentFormat: DatevAssignmentFormat;
    /**
     * REV-18627 (C1conflict, ADR-0001/E1): das Format des KONTENBLATTS, gelesen an `Bilanz`/`GuV`!C1
     * — unabhängig von `assignmentFormat` (Zuordnungsliste, `Postenstruktur`!H1). Für eine
     * Masterdatei gelten beide getrennt; widersprechen sie sich, gewinnt je Seite ihr eigener
     * Spaltenkopf und der Widerspruch wird über `hasFormatContradiction` gemeldet.
     * Stub — noch nicht gesetzt (`undefined`), siehe Contract C1(z1)/(z2).
     */
    accountsAssignmentFormat?: DatevAssignmentFormat;
    hasFormatContradiction: boolean;
}

@Injectable()
export class DatevExcelHelper implements ExcelHelper {
    spreadSheetData: ISpreadSheet;

    /** REV-18627 (C1/§5.2) Instanzzustand der letzten isKnownFileFormat-Erkennung. */
    private formatDetection?: DatevFormatDetection;

    /**
     * Liefert das Zuordnungsformat der zuletzt an isKnownFileFormat übergebenen Datei
     * (Instanzzustand, seams §5.2 — getImportSettings ist parameterlos).
     */
    public getDetectedAssignmentFormat(): DatevAssignmentFormat {
        return this.formatDetection?.assignmentFormat ?? 'accountNumber';
    }

    /** Volle Erkennung inklusive gemeldetem Widerspruch Infodaten↔Spaltenkopf. */
    public getFormatDetection(): DatevFormatDetection {
        return this.formatDetection;
    }

    /**
     * REV-18627 (C1conflict, ADR-0001/E1): die Zuordnungsliste (welches Regelwerk die Kaskade
     * benutzt) hängt AUSSCHLIESSLICH an `Postenstruktur`!H1 — unabhängig vom Kontenblatt.
     * `undefined`, wenn die Datei keine Postenstruktur trägt (reine Kontendatei).
     */
    private detectAssignmentFormatFromTaxonomySheet(): DatevAssignmentFormat | undefined {
        const taxonomySheet = this.spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_STRUCTURE_SHEET,
        );
        if (!taxonomySheet) return undefined;
        return taxonomySheet.rows[0]?.cells[7] === 'Kontenzweckzuordnung' ? 'purpose' : 'accountNumber';
    }

    /**
     * REV-18627 (C1conflict, ADR-0001/E1): das Format des KONTENBLATTS (welche Spalten
     * `getImportSettings` benutzt) hängt AUSSCHLIESSLICH an `Bilanz`!C1 — unabhängig von der
     * Postenstruktur. `undefined`, wenn kein Kontenblatt gefunden wird (sollte bei einer erkannten
     * DATEV-Datei nicht vorkommen).
     */
    private detectAssignmentFormatFromAccountSheet(): DatevAssignmentFormat | undefined {
        const balanceSheet = this.spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
        );
        if (!balanceSheet) return undefined;
        return balanceSheet.rows[0]?.cells[2] === 'KontenzweckRefNr' ? 'purpose' : 'accountNumber';
    }

    /** Gegenprobe an der Markerzeile `Infodaten` "Jahresabschluss" — nur als Widerspruchs-Signal. */
    private detectAssignmentFormatFromInfodaten(): DatevAssignmentFormat | undefined {
        const infoSheet = this.spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.INFO_SHEET,
        );
        const marker = infoSheet?.rows.find((row) => row != null && String(row.cells[0]) === 'Jahresabschluss');
        const value = marker?.cells[1];
        if (value === 'Kontenzwecke') return 'purpose';
        if (value === 'ZOT') return 'accountNumber';
        return undefined;
    }

    private computeFormatDetection(fileType: PartnerExcelImportFileType): DatevFormatDetection {
        // REV-18627 (C1conflict, ADR-0001/E1): Zuordnungsliste und Kontenblatt werden UNABHÄNGIG
        // voneinander erkannt (je ihr eigener Spaltenkopf); fällt eine Seite aus (keine
        // Postenstruktur bzw. kein Kontenblatt), gilt die jeweils andere als Rückfall.
        const taxonomyFormat = this.detectAssignmentFormatFromTaxonomySheet();
        const accountSheetFormat = this.detectAssignmentFormatFromAccountSheet();
        const assignmentFormat = taxonomyFormat ?? accountSheetFormat ?? 'accountNumber';
        const accountsAssignmentFormat = accountSheetFormat ?? taxonomyFormat ?? 'accountNumber';
        const infodatenFormat = this.detectAssignmentFormatFromInfodaten();
        const contradictsInfodaten = infodatenFormat != null && infodatenFormat !== assignmentFormat;
        const contradictsBetweenColumnHeaders =
            taxonomyFormat != null && accountSheetFormat != null && taxonomyFormat !== accountSheetFormat;
        return {
            fileType,
            assignmentFormat,
            accountsAssignmentFormat,
            hasFormatContradiction: contradictsInfodaten || contradictsBetweenColumnHeaders,
        };
    }

    public isKnownFileFormat(spreadSheetData: ISpreadSheet): PartnerExcelImportFileType {
        this.spreadSheetData = spreadSheetData;

        let fileType: PartnerExcelImportFileType;
        if (this.isMasterDataImportFile()) {
            fileType = PartnerExcelImportFileType.DATEV_MASTER;
        } else if (this.isAccountImportFile()) {
            fileType = PartnerExcelImportFileType.DATEV_ACCOUNTS;
        } else {
            fileType = PartnerExcelImportFileType.UNKNOWN;
        }
        this.formatDetection = this.computeFormatDetection(fileType);
        return fileType;
    }

    private isMasterDataImportFile(): boolean {
        if (this.spreadSheetData.sheets.length <= 4) {
            return false;
        }

        const sheetNames = this.spreadSheetData.sheets.map((sheet) => sheet.name);
        const requiredSheets = [
            MASTER_DATA_DEFAULT_SHEET_NAMES.INFO_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_STRUCTURE_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_INFO_SHEET,
        ];

        // Check if all required sheets exist
        const hasAllSheets = requiredSheets.every((name) => sheetNames.includes(name));
        if (!hasAllSheets) {
            return false;
        }

        // Find the INFO_SHEET and check the version cell
        const infoSheet = this.spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.INFO_SHEET,
        );

        return infoSheet?.rows[1]?.cells[0] === MASTER_DATA_DEFAULT_SHEET_NAMES.VERSION_SHEET;
    }

    private isAccountImportFile(): boolean {
        if (this.spreadSheetData.sheets.length < 3) {
            return false;
        }

        const sheetNames = this.spreadSheetData.sheets.map((sheet) => sheet.name);
        const requiredSheets = [
            MASTER_DATA_DEFAULT_SHEET_NAMES.INFO_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
            MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
        ];

        // Check if all required sheets exist
        const hasAllSheets = requiredSheets.every((name) => sheetNames.includes(name));
        if (!hasAllSheets) {
            return false;
        }

        // Find the INFO_SHEET and check the required cells
        const infoSheet = this.spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.INFO_SHEET,
        );

        return (
            infoSheet?.rows[1]?.cells[0] === MASTER_DATA_DEFAULT_COLUMN_NAMES.VERSION &&
            infoSheet?.rows[2]?.cells[0] === MASTER_DATA_DEFAULT_COLUMN_NAMES.START_FINANCIAL_YEAR &&
            infoSheet?.rows[3]?.cells[0] === MASTER_DATA_DEFAULT_COLUMN_NAMES.END_FINANCIAL_YEAR
        );
    }

    getImportSettings(): ExcelImportDefinitions {
        // REV-18627 (B9/C1(e)/(t)): im Zweck-Format rueckt der Kontenzweck auf Spalte C, Saldo/
        // Vorjahr je eine Spalte weiter (D/E), Gesellschafter auf F — in Bilanz UND GuV gleich.
        // REV-18627 (C1conflict, ADR-0001/E1): das Spaltenlayout gehört ans KONTENBLATT
        // (`accountsAssignmentFormat`), nicht an die Zuordnungsliste (`assignmentFormat` /
        // `getDetectedAssignmentFormat()` — die bleibt für den Engine-Getter/die Kaskade).
        // Review R5: das Layout hängt am Instanzzustand, den nur `isKnownFileFormat()` setzt. Ein
        // Aufrufer ohne diesen vorherigen Aufruf bekäme still das Nummern-Layout — im Zweck-Format
        // also Salden 0 (der ursprüngliche Kundenfehler). Darum: laut scheitern statt still raten.
        if (this.formatDetection == null) {
            throw new Error(
                'DatevExcelHelper.getImportSettings(): isKnownFileFormat(spreadSheet) muss zuerst aufgerufen werden — das Spaltenlayout hängt an der dort erkannten Datei.',
            );
        }
        const isPurposeFormat = this.formatDetection.accountsAssignmentFormat === 'purpose';
        // `id` traegt die tatsaechliche Spaltenposition (financial-statement-import-form.helper.ts
        // rechnet nur mit `.id`, `.name` ist nur die Anzeige) — im Zweck-Format rueckt jede Spalte
        // um eins nach rechts (Kontenzweck belegt C).
        const balanceCol = isPurposeFormat
            ? { id: COLUMN_NAME_IDENTIFICATION.FIVE, name: COLUMN_NAME_IDENTIFICATION.D }
            : { id: COLUMN_NAME_IDENTIFICATION.FOUR, name: COLUMN_NAME_IDENTIFICATION.C };
        const previousYearCol = isPurposeFormat
            ? { id: COLUMN_NAME_IDENTIFICATION.SIX, name: COLUMN_NAME_IDENTIFICATION.E }
            : { id: COLUMN_NAME_IDENTIFICATION.FIVE, name: COLUMN_NAME_IDENTIFICATION.D };
        const shareholderCol = isPurposeFormat
            ? { id: '7', name: COLUMN_NAME_IDENTIFICATION.F }
            : { id: COLUMN_NAME_IDENTIFICATION.SIX, name: COLUMN_NAME_IDENTIFICATION.E };
        const purposeColNo = isPurposeFormat
            ? { id: COLUMN_NAME_IDENTIFICATION.FOUR, name: COLUMN_NAME_IDENTIFICATION.C }
            : undefined;

        const datevBS: IFinancialStatementImportDefinition = {
            sheetName: {
                id: COLUMN_NAME_IDENTIFICATION.ONE,
                name: MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
            },
            accountsStartingRow: 2,
            accountNameCol: {
                id: COLUMN_NAME_IDENTIFICATION.THREE,
                name: COLUMN_NAME_IDENTIFICATION.B,
            },
            accountNoCol: {
                id: COLUMN_NAME_IDENTIFICATION.TWO,
                name: COLUMN_NAME_IDENTIFICATION.A,
            },
            balanceColNo: balanceCol,
            previousYearColNo: previousYearCol,
            shareholderColNo: shareholderCol,
            purposeColNo,
        };
        const datevIS: IFinancialStatementImportDefinition = {
            sheetName: {
                id: COLUMN_NAME_IDENTIFICATION.ONE,
                name: MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            },
            accountsStartingRow: 2,
            accountNameCol: {
                id: COLUMN_NAME_IDENTIFICATION.THREE,
                name: COLUMN_NAME_IDENTIFICATION.B,
            },
            accountNoCol: {
                id: COLUMN_NAME_IDENTIFICATION.TWO,
                name: COLUMN_NAME_IDENTIFICATION.A,
            },
            balanceColNo: balanceCol,
            previousYearColNo: previousYearCol,
            shareholderColNo: shareholderCol,
            purposeColNo,
        };
        const datevInterpretation: IFinancialStatementInterpretation = {
            numberFormat: NumberFormats.SINGLE,
            assets: Signs.POSITIVE,
            liabilities: Signs.NEGATIVE,
            income: Signs.POSITIVE,
            expenses: Signs.NEGATIVE,
        };
        const datevAccountFilter: IAccountFilter = {
            accountNumber: '',
            accountName: '',
            openingValue: '',
            closingValue: '',
            excludedWord: '',
            includedWord: '',
            removeAccountWithZeroBalance: true,
        };
        const response: ExcelImportDefinitions = {
            balanceSheet: datevBS,
            incomeStatement: datevIS,
            normalization: datevInterpretation,
            filter: datevAccountFilter,
        };
        return response;
    }

    /**
     * Extracts and preserves extra Excel content (like Sammelkonten/Kontokorrent)
     * that should be re-exported without modification for audit compliance.
     *
     * @param spreadSheetData The Excel data to analyze
     * @returns ExtraExcelContent with encrypted sheet data or null if no extra content found
     */
    extractExtraExcelContent(spreadSheetData: ISpreadSheet): IExtraExcelContent | null {
        const extraContent: IExtraExcelContent = {};
        let hasExtraContent = false;

        // List of sheet names that should be preserved (case-insensitive)
        const preserveSheetNames = [
            'sammelkonten',
            'kontokorrent',
            'sammelbuchungen',
            'nebenbuchhaltung',
            'zusatzdaten',
        ];

        // Check each sheet in the spreadsheet
        if (spreadSheetData.sheets) {
            for (const sheet of spreadSheetData.sheets) {
                const sheetNameLower = sheet.name?.toLowerCase() || '';

                // Check if this sheet should be preserved
                const shouldPreserve = preserveSheetNames.some((preserveName) => sheetNameLower.includes(preserveName));

                if (shouldPreserve) {
                    debug(`Found sheet to preserve: ${sheet.name}`);

                    try {
                        // Convert sheet data to Base64 encoded string for secure storage
                        const sheetContent = this.encodeSheetContent(sheet);
                        if (sheetContent) {
                            extraContent[sheet.name] = sheetContent;
                            hasExtraContent = true;
                        }
                    } catch (error) {
                        debug(`Error encoding sheet ${sheet.name}:`, error);
                        // Continue processing other sheets even if one fails
                    }
                }
            }
        }

        return hasExtraContent ? extraContent : null;
    }

    /**
     * Encodes sheet content as Base64 for secure storage and later re-export.
     * This preserves the complete Excel sheet structure with full formatting.
     *
     * @param sheet The sheet to encode
     * @returns Base64 encoded sheet content or null if encoding fails
     */
    private encodeSheetContent(sheet: ISheet): string | null {
        try {
            // Get the raw worksheet from SheetJS workbook for complete preservation
            const rawWorksheet = sheet.rawWorkbook?.Sheets?.[sheet.name];

            // Add debugging to verify this contains styling information
            console.log('Raw worksheet cell A1:', rawWorksheet?.['A1']);

            if (!rawWorksheet) {
                debug(`No raw worksheet found for sheet: ${sheet.name}`);
                return null;
            }

            const sheetData = {
                name: sheet.name,
                // Store the complete SheetJS worksheet object with all formatting
                rawWorksheet: rawWorksheet,
                // Optional: keep processed rows for debugging/fallback
                processedRows: sheet.rows || [],
            };

            // Convert to JSON then encode as Base64 with UTF-8 support
            const jsonString = JSON.stringify(sheetData);
            const utf8Bytes = new TextEncoder().encode(jsonString);

            // Convert bytes to string without spread operator (avoids stack overflow)
            let binaryString = '';
            for (let i = 0; i < utf8Bytes.length; i++) {
                binaryString += String.fromCharCode(utf8Bytes[i]);
            }

            return btoa(binaryString);
        } catch (error) {
            debug(`Failed to encode sheet content for ${sheet.name}:`, error);
            return null;
        }
    }

    createTaxonomy(
        spreadSheetData: ISpreadSheet,
        canUseUncovershorfall: boolean,
        canUseEquity: boolean,
    ): TaxonomyDefinitionsWithErrorInformation {
        const errors: DATEV_ERROR[] = new Array<DATEV_ERROR>();

        const taxonmyInformation = spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_INFO_SHEET,
        );

        if (!taxonmyInformation) {
            // REV-18627 (B5, AC 6): eine reine Kontendatei (kein Posteninfo UND keine
            // Postenstruktur) ist erwartungsgemäss ohne Taxonomie — kein Fehler.
            const hasTaxonomyStructure = spreadSheetData.sheets.some(
                (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_STRUCTURE_SHEET,
            );
            if (!hasTaxonomyStructure) {
                return {
                    taxonomyDefinitions: null,
                    errors,
                };
            }
            errors.push(DATEV_ERROR.MISSING_TEMPLATE_INFO);
            return {
                taxonomyDefinitions: null,
                errors,
            };
            // or handle the missing sheet appropriately
        }
        const emptyText = '<keine Angabe';
        const annualSurplusFliName = taxonmyInformation.rows[3].cells[1];
        const profitFliName = taxonmyInformation.rows[4].cells[1];
        const cashFliName = taxonmyInformation.rows[5].cells[1];

        let uncoverShortFallAssets = undefined;
        let uncoverShortFallLiabilities = undefined;
        let equityGroupName = undefined;
        if (canUseUncovershorfall) {
            uncoverShortFallAssets =
                taxonmyInformation.rows[0].cells[1] == emptyText ? undefined : taxonmyInformation.rows[0].cells[1];
            uncoverShortFallLiabilities =
                taxonmyInformation.rows[1].cells[1] == emptyText ? undefined : taxonmyInformation.rows[1].cells[1];
        }
        if (canUseEquity || canUseUncovershorfall) {
            equityGroupName =
                taxonmyInformation.rows[2].cells[1] == emptyText ? undefined : taxonmyInformation.rows[2].cells[1];
        }
        const specialFlis: SpecialFLIs = {
            equityGroupName: equityGroupName,
            annualSurplus: annualSurplusFliName,
            profitOfTheYear: profitFliName,
            uncoverShortFallAssets: uncoverShortFallAssets,
            uncoverShortFallLiabilities: uncoverShortFallLiabilities,
            cashFlow: cashFliName,
        };

        const taxonomySheet = spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_STRUCTURE_SHEET,
        );

        if (!taxonomySheet) {
            errors.push(DATEV_ERROR.MISSING_TEMPLATE_STRUCTURE);
            return {
                taxonomyDefinitions: null,
                errors,
            };
            // or handle the missing sheet appropriately
        }
        //Check if this file has the DATEV format or if it has been modified by aumico to simplify the import
        const format = this.checkTaxonomyFormat(taxonomySheet);
        if (format == 'aumico') {
            try {
                return this.createTaxonomyFromAumicoFile(taxonomySheet, specialFlis, errors);
            } catch (error) {
                throw error;
            }
        } else if (format == 'Unknown') {
            throw new Error('This is an unknown file format');
        }

        const rowsWithIssues = []; // Array to store rows with issues

        const processedRows = taxonomySheet.rows
            .map((row: ISheetRow) => {
                // Check if 'level' can be converted to a number
                const level = Number(row.cells[2]);
                const mainCol = Number(row.cells[4]);
                const preCol = Number(row.cells[5]);
                const ofThatCol = Number(row.cells[6]);

                if (!isNaN(level)) {
                    // If 'level' is a valid number
                    const response: DatevRows = {
                        evaluation: row.cells[0],
                        balanceType: row.cells[1],
                        level,
                        // REV-18627 (D1): Postenbezeichnung getrimmt — der Import baut node.id
                        // daraus (item.name/accountAssignments-Schlüssel), nie ungetrimmt.
                        itemText: (row.cells[3] ?? '').trim(),
                        mainCol,
                        preCol,
                        ofThatCol,
                        accountAssignments: [],
                    };

                    // Start from the 8th column (index 7) and loop until an empty cell is found.
                    // REV-18627 (B11): Dedupe je Zeile über den vollständigen Rohwert (mit Suffix).
                    const seenAssignments = new Set<string>();
                    for (let colIndex = 7; colIndex < row.cells.length; colIndex++) {
                        if (row.cells[colIndex] !== '') {
                            const value = row.cells[colIndex];
                            if (!seenAssignments.has(value)) {
                                seenAssignments.add(value);
                                response.accountAssignments.push(value);
                            }
                        } else {
                            break; // Break the loop if an empty cell is found
                        }
                    }
                    return response;
                } else {
                    // If 'level' is not a valid number, add the row to the issues array
                    rowsWithIssues.push({
                        rowIndex: taxonomySheet.rows.indexOf(row),
                        rowContent: row,
                    });
                    return null;
                }
            })
            .filter((response) => response !== null); // Filter out null responses

        const balanceRows = processedRows.filter(
            (row) => row.evaluation === MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
        );
        const assets = balanceRows.filter((row) => row.balanceType === BALANCE_TYPE_ASSETS);
        const liabilitiesAndEquity = balanceRows.filter((row) => row.balanceType === BALANCE_TYPE_LIABILITIES);
        const incomeRow = processedRows.filter(
            (row) => row.evaluation === MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
        );

        // REV-22122: the purpose export marks its sums differently (see `createTree`).
        const treeOptions = { purposeFormat: format === DATEV_TAXONOMY_FORMAT_PURPOSE };
        const assetsTree = this.createTree(assets, specialFlis, PREFIX_BALANCE_ASSETS_ITEM, errors, treeOptions);
        if (assetsTree.length == 0) {
            errors.push(DATEV_ERROR.WRONG_ASSETS_DATA);
        }
        if (specialFlis.uncoverShortFallAssets) {
            assetsTree.push({
                id: specialFlis.uncoverShortFallAssetsId,
                data: {
                    evaluation: MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
                    balanceType: BALANCE_TYPE_ASSETS,
                    level: 0,
                    itemText: specialFlis.uncoverShortFallAssets,
                    mainCol: 0,
                    preCol: 0,
                    ofThatCol: 0,
                    accountAssignments: [],
                },
                children: [],
                type: NodeType.FLI,
            });
        }
        const liabiltiesTree = this.createTree(
            liabilitiesAndEquity,
            specialFlis,
            PREFIX_BALANCE_LIABILITIES_ITEM,
            errors,
            treeOptions,
        );
        if (liabiltiesTree.length == 0) {
            errors.push(DATEV_ERROR.WRONG_LIBILTIE_DATA);
        }
        if (specialFlis.uncoverShortFallLiabilities) {
            // uncoverShortFallLiabilities needs to be added to Equity
            const equityGroupId = specialFlis.equityGroupNameId;
            const equityNode = findNodeById(liabiltiesTree, equityGroupId);
            if (equityNode) {
                equityNode.children.push({
                    id: specialFlis.uncoverShortFallLiabilitiesId,
                    data: {
                        evaluation: MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
                        balanceType: BALANCE_TYPE_LIABILITIES,
                        level: equityNode.data.level + 1,
                        itemText: specialFlis.uncoverShortFallLiabilities,
                        mainCol: 0,
                        preCol: 0,
                        ofThatCol: 0,
                        accountAssignments: [],
                    },
                    children: [],
                    type: NodeType.FLI,
                });
                debug(' Equity Node found');
            } else {
                debug('No Equity Node exists');
            }
        } else {
            debug('No uncoverShortFallLiabilities Node exists');
        }
        const incomeTree = this.createTree(incomeRow, undefined, PREFIX_INCOME_ITEM, errors, treeOptions);
        if (incomeTree.length == 0) {
            errors.push(DATEV_ERROR.WRONG_INCOME_DATA);
        }
        // Get all account assignments
        const accountMapping1 = getAccountAssignments(assetsTree);
        const accountMapping2 = getAccountAssignments(liabiltiesTree);
        const accountAssignmentBalanceSheet = mergeAccountMappings(accountMapping1, accountMapping2);
        const accountAssignmentIncomeStatment = getAccountAssignments(incomeTree);

        // TODO here we are going to create a new taxonomy which is not saved in the templates microservices.
        // Mapping Tree to ITemplateEntityDTO
        // Initial group setup for the root of the template
        const assetsGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.ASSETS,
            title: BALANCE_TYPE_ASSETS,
            totalName: BALANCE_TYPE_ASSETS,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };

        const convertedAssets = this.convertTreeToIAccountMappingTemplateGroup(
            assetsTree,
            assetsGroup,
            FinancialLineItemType.ASSETS,
            specialFlis,
            true,
        );

        const liabGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.LIABILITIES,
            title: BALANCE_TYPE_LIABILITIES,
            totalName: BALANCE_TYPE_LIABILITIES,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };
        const convertedLiabilities = this.convertTreeToIAccountMappingTemplateGroup(
            liabiltiesTree,
            liabGroup,
            FinancialLineItemType.LIABILITIES,
            specialFlis,
            true,
        );

        const incomeGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.PROFIT_OF_THE_YEAR,
            title: MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            totalName: MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };

        const convertedIncome = this.convertTreeToIAccountMappingTemplateGroup(
            incomeTree,
            incomeGroup,
            FinancialLineItemType.INCOME,
            specialFlis,
            false,
        );

        const title = MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET;
        const totalName = 'Bilanzsumme';

        const balanceGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.BALANCE_SHEET,
            title: title,
            totalName: totalName,
            hideTotal: false,
            groups: [convertedAssets, convertedLiabilities],
            items: [],
            sortedItems: [convertedAssets, convertedLiabilities],
        };

        const balanceGroupWithSortedItemsNameType = SortingItemsHelper.mapGroupSortedItemsToItemNameType(balanceGroup);
        const incomeGroupWithSortedItemsNameType =
            SortingItemsHelper.mapGroupSortedItemsToItemNameType(convertedIncome);

        const accountMappingBS: IAccountMappingMap = mapToIAccountMappingMap(accountAssignmentBalanceSheet);
        const accountMappingIS: IAccountMappingMap = mapToIAccountMappingMap(accountAssignmentIncomeStatment);

        const groupsOfAssets = this.getAllGroups(assetsTree);
        const groupsOfLia = this.getAllGroups(liabiltiesTree);
        const allgroups = []
            .concat(...groupsOfAssets, ...groupsOfLia)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((group) => group.name.trim() !== '');

        const flisAssets = this.getAllFlis(assetsTree, specialFlis)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');
        const flisLia = this.getAllFlis(liabiltiesTree, specialFlis)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');
        const allFlis = []
            .concat(...[flisAssets, flisLia])
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');
        // REV-18627 (B2/B11): Format-Flag reist mit der Vorlage (E3-Heimat: IAnnualReport.importedTaxonomy).
        const accountAssignmentsFormat: DatevAssignmentFormat =
            format === DATEV_TAXONOMY_FORMAT_PURPOSE ? 'purpose' : 'accountNumber';
        // REV-22122: the DATEV side of every item (Aktiva/Passiva/Aufwand/Ertrag) travels with the
        // template like `accountAssignmentsFormat` — the income tree gives every item `fliType:
        // income`, so without it the mapping cannot tell an expense item from an income item when a
        // key sits with `-D` on both (netting groups, accountMappingFormHelper.ts).
        const balanceItemSides = collectItemSides([...assetsTree, ...liabiltiesTree]);
        const incomeItemSides = collectItemSides(incomeTree);

        const balanceTaxonomy: IAccountMappingTemplate & DatevTemplateSides = {
            group: balanceGroupWithSortedItemsNameType,
            name: 'Balance Sheet',
            type: TemplateType.BALANCE,
            accountBasis: '',
            legalForm: '',
            equityGroupName: specialFlis.equityGroupNameId,
            annualSurplusFli: specialFlis.annualSurplusId,
            profitOfTheYear: specialFlis.profitOfTheYearId,
            uncoveredShortfallAssetsFli: specialFlis.uncoverShortFallAssetsId,
            uncoveredShortfallLiabilitiesFli: specialFlis.uncoverShortFallLiabilitiesId,
            accountAssignments: accountMappingBS,
            accountAssignmentsFormat,
            accountItemSides: balanceItemSides,
            flis: allFlis,
            flisAssets: flisAssets,
            flisLia: flisLia,
            groups: allgroups,
        };
        const incomeTaxonomy: IAccountMappingTemplate & DatevTemplateSides = {
            group: incomeGroupWithSortedItemsNameType,
            name: 'Income Sheet',
            type: TemplateType.INCOME,
            accountBasis: '',
            legalForm: '',
            equityGroupName: specialFlis.equityGroupNameId,
            annualSurplusFli: specialFlis.annualSurplusId,
            profitOfTheYear: specialFlis.profitOfTheYearId,
            uncoveredShortfallAssetsFli: specialFlis.uncoverShortFallAssetsId,
            uncoveredShortfallLiabilitiesFli: specialFlis.uncoverShortFallLiabilitiesId,
            accountAssignments: accountMappingIS,
            accountAssignmentsFormat,
            accountItemSides: incomeItemSides,
        };

        return {
            taxonomyDefinitions: {
                rawBalanceTemplate: balanceTaxonomy,
                rawIncomeTemplate: incomeTaxonomy,
            },
            errors,
        };
    }

    private convertTreeToIAccountMappingTemplateGroup(
        rootNodes: TreeNode[],
        rootGroup: IAccountMappingTemplateGroup,
        fliType: FinancialLineItemType,
        specialFlis: SpecialFLIs,
        disableNumberingForSumsByDefault: boolean,
    ): IAccountMappingTemplateGroup {
        const convertNode = (
            node: TreeNode,
            parentGroup?: IAccountMappingTemplateGroup,
            parentItem?: AccountMappingTemplateGroupItem,
        ): void => {
            if (node.type === NodeType.GROUP) {
                // Create Group
                const group: IAccountMappingTemplateGroup = {
                    name: node.id,

                    title: node.data.itemText,
                    totalName: node.data.itemText,
                    hideTotal: true,
                    isHiddenGroup: false,
                    groups: [],
                    items: [],
                    sortedItems: [],
                };
                if (parentGroup != null) {
                    const sortedItems = parentGroup.sortedItems as SortedMappingItem[];
                    parentGroup.groups.push(group);
                    sortedItems.push(group);
                    parentGroup.sortedItems = sortedItems;
                }

                // Convert children nodes
                node.children.forEach((childNode) => convertNode(childNode, group));
            } else if (node.type === NodeType.HIDDEN_GROUP) {
                // Create Group
                const group: IAccountMappingTemplateGroup = {
                    name: node.id,
                    title: 'Einrückung ' + node.data.itemText,
                    totalName: node.data.itemText,
                    hideTotal: true,
                    isHiddenGroup: true,
                    groups: [],
                    items: [],
                    sortedItems: [],
                };
                if (parentGroup != null) {
                    const sortedItems = parentGroup.sortedItems as SortedMappingItem[];
                    parentGroup.groups.push(group);
                    sortedItems.push(group);
                    parentGroup.sortedItems = sortedItems;
                }
                // Convert children nodes
                node.children.forEach((childNode) => convertNode(childNode, group));
            } else if (node.type === NodeType.FLI || node.type === NodeType.FLI_WITHOUT_NUMBERING) {
                // Create Item
                const item: IAccountMappingTemplateGroupItemFLI = {
                    name: node.id,
                    caption: node.data.itemText,
                    type: ItemType.FLI,
                    fliType,
                    accounts: [],
                    reclassesAccounts: [],
                };
                parentGroup?.items.push(item);
                parentGroup?.sortedItems.push(item);
                // Convert children nodes
                node.children.forEach((childNode) => convertNode(childNode, undefined, item));
            } else if (node.type === NodeType.OF_THAT_ITEM) {
                // Create Item
                const ofthatitem: IAccountMappingSubFLI = {
                    type: ItemType.OF_THAT_ITEM,
                    name: node.id,
                    caption: node.data.itemText,
                    accounts: [],
                    reclassesAccounts: [],
                };
                // Add item creation logic here, pushing to parentGroup.items
                (parentItem as IAccountMappingTemplateGroupItemFLI).accounts.push(ofthatitem);
            } else if (node.type === NodeType.SUM) {
                //profitOfTheYear1 must always be an FLI
                if (node.id === specialFlis.profitOfTheYearId || node.id === specialFlis.annualSurplusId) {
                    const item: IAccountMappingTemplateGroupItemFLI = {
                        name: node.id,
                        caption: node.data.itemText,
                        type: ItemType.FLI,
                        fliType,
                        accounts: [],
                        reclassesAccounts: [],
                    };

                    parentGroup?.items.push(item);
                    parentGroup?.sortedItems.push(item);
                    // Convert children nodes
                    node.children.forEach((childNode) => convertNode(childNode, undefined, item));
                } else {
                    // Create Sum
                    const scope: IItemNameType = {
                        name: parentGroup.name,
                        type: ItemType.GROUP,
                    };
                    const item: AccountMappingTemplateGroupItem = {
                        name: node.id,
                        caption: node.data.itemText,
                        fliType,
                        type: ItemType.SUB_TOTAL,
                        scope,
                        disableNumbering: disableNumberingForSumsByDefault,
                    };
                    parentGroup?.items.push(item);
                    parentGroup?.sortedItems.push(item);
                    // Convert children nodes
                    node.children.forEach((childNode) => convertNode(childNode, undefined, item));
                }

                // Add sum creation logic here
            }
            // Handle other types if needed
        };

        // Convert each root node
        rootNodes.forEach((node) => convertNode(node, rootGroup));

        return rootGroup;
    }

    private getAllGroups(rootNodes: TreeNode[]): INameId[] {
        const getGroups = (node: TreeNode): INameId[] => {
            const allgroups: INameId[] = [];
            if (node.type === NodeType.HIDDEN_GROUP) {
                allgroups.push(...node.children.flatMap((childNode) => getGroups(childNode)));
            }
            if (node.type === NodeType.GROUP) {
                // Create Group
                const group: INameId = {
                    id: node.id,
                    name: node.data.itemText,
                };
                // Convert children nodes
                allgroups.push(group);
                allgroups.push(...node.children.flatMap((childNode) => getGroups(childNode)));
            }
            return allgroups;
        };

        // Return a flat array with all groups
        return rootNodes.flatMap((node) => getGroups(node));
    }
    private getAllFlis(rootNodes: TreeNode[], specialFlis: SpecialFLIs): INameId[] {
        const getFlis = (node: TreeNode): INameId[] => {
            const allitems: INameId[] = [];
            if (node.type === NodeType.GROUP || node.type === NodeType.HIDDEN_GROUP) {
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            if (node.id === specialFlis.profitOfTheYearId || node.id === specialFlis.annualSurplusId) {
                const item: INameId = {
                    id: node.id,
                    name: node.data.itemText,
                };
                allitems.push(item);
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            if (node.type === NodeType.FLI || node.type === NodeType.FLI_WITHOUT_NUMBERING) {
                const item: INameId = {
                    id: node.id,
                    name: node.data.itemText,
                };
                allitems.push(item);
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            return allitems;
        };

        // Return a flat array with all groups
        return rootNodes.flatMap((node) => getFlis(node));
    }
    private getAllGroupsFromAumicoNode(rootNodes: AumicoNode[]): INameId[] {
        const getGroups = (node: AumicoNode): INameId[] => {
            const allgroups: INameId[] = [];
            if (node.classification === AumicoNodeType.HIDDEN_GROUP) {
                allgroups.push(...node.children.flatMap((childNode) => getGroups(childNode)));
            }
            if (node.classification === AumicoNodeType.GROUP) {
                // Create Group
                const group: INameId = {
                    id: node.id,
                    name: node.itemText,
                };
                // Convert children nodes
                allgroups.push(group);
                allgroups.push(...node.children.flatMap((childNode) => getGroups(childNode)));
            }
            return allgroups;
        };

        // Return a flat array with all groups
        return rootNodes.flatMap((node) => getGroups(node));
    }
    private getAllFlisFromAumicoNode(rootNodes: AumicoNode[], specialFlis: SpecialFLIs): INameId[] {
        const getFlis = (node: AumicoNode): INameId[] => {
            const allitems: INameId[] = [];
            if (node.classification === AumicoNodeType.GROUP || node.classification === AumicoNodeType.HIDDEN_GROUP) {
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            if (node.id === specialFlis.profitOfTheYearId || node.id === specialFlis.annualSurplusId) {
                const item: INameId = {
                    id: node.id,
                    name: node.itemText,
                };
                allitems.push(item);
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            if (
                node.classification === AumicoNodeType.FLI ||
                node.classification === AumicoNodeType.FLI_WITHOUT_NUMBERING ||
                node.classification === AumicoNodeType.X_FLI
            ) {
                const item: INameId = {
                    id: node.id,
                    name: node.itemText,
                };
                allitems.push(item);
                allitems.push(...node.children.flatMap((childNode) => getFlis(childNode)));
            }
            return allitems;
        };

        // Return a flat array with all groups
        return rootNodes.flatMap((node) => getFlis(node));
    }
    /***
   Here some information you need to know:
    - OF-THAT-ITEMS are always a child of a FLI
    - GROUPS have no values in accountAssignment and cannot be empty, and mainCol = 0
    - FLI is a child of a GROUP or directly assigned in the rootNodes
    - FLI can have values in accountAssignment, but it's also possible that they don't have accountAssignment
 */
    private createTree(
        data: DatevRows[],
        specialFlis: SpecialFLIs | undefined,
        prefix: string,
        errors: DATEV_ERROR[],
        options?: { purposeFormat?: boolean },
    ): TreeNode[] {
        // REV-22122: the account-purpose export marks its sums differently from the account-number
        // export (see `purposeSumParentLevel` below and the "GROUP or SUM" step).
        const purposeFormat = options?.purposeFormat === true;
        const rootNodes: TreeNode[] = [];
        const nodesMap = new Map<number, TreeNode>();
        const flatNodeArray = new Array<TreeNode>();
        // REV-18627 (I2): der zuletzt registrierte "echte" Hierarchieknoten (levelType 0) —
        // Rückfall-Vorfahre, wenn eine Zeile ihren exakten Elternschlüssel nicht findet (siehe
        // unten).
        let lastRealNode: TreeNode | undefined;
        // REV-22122: level of the last regular (levelType 0) row — where a purpose-format sum sits.
        let lastRealLevel = 0;

        let startAt: number = 99;
        data.forEach((row, index) => {
            if (ITEMS_TO_INGORE.includes(row.itemText)) return;
            // REV-18627 (I2): SUM- (9x) und OF_THAT- (8x) Zeilen tragen keinen echten
            // Hierarchie-Level — ihr Rest-Digit kann kleiner sein als der tatsächliche oberste
            // Level (z. B. Zweck-Postenstruktur GuV: SUM-Zeilen mit Level 91 neben echten
            // Top-Zeilen auf Level 3). Nur levelType===0-Zeilen bestimmen den Start-Level.
            if (Math.floor(row.level / 10) !== 0) return;
            let levelValue = row.level % 10;
            if (levelValue == 0) return;
            if (levelValue < startAt) {
                startAt = levelValue;
            }
        });
        if (startAt < 1) {
            startAt = 1;
        }
        data.forEach((row, index) => {
            const id = prefix + index.toString().padStart(6, '0') + row.itemText;

            // replace the value of specialFlis to its new name
            if (specialFlis) {
                for (const key in specialFlis) {
                    if (!key.endsWith('Id')) {
                        // REV-22122: the annual result and surplus items also by the purpose export's
                        // "A / B" reading; the shortfall items stay exact — their narrower reading is
                        // the same text on both sides and would let the liabilities row overwrite the
                        // assets one.
                        const names = TOLERANT_SPECIAL_ITEMS.includes(key)
                            ? specialItemNameCandidates(specialFlis[key])
                            : [specialFlis[key]];
                        if (names.includes(row.itemText)) {
                            specialFlis[key + 'Id'] = id;
                            break;
                        }
                    }
                }
            }

            const node: TreeNode = {
                id,
                data: row,
                children: [],
                type: row.level % 10 === 0 ? NodeType.IGNORE : undefined,
            };

            const levelType = Math.floor(row.level / 10); // Determine the type based on the level
            let levelValue = row.level % 10;

            // Assign type to leaves based on the level
            if (levelType === 9 && purposeFormat && row.accountAssignments.length === 0) {
                // REV-22122: in the account-purpose export a sum is a 9x row WITHOUT main column (the
                // total stands in column G, "66"), and its digit names the level of the group it
                // closes: "Summe Sachanlagen" 92 closes the level-2 group Sachanlagen, "Summe
                // Anlagevermögen" 91 the level-1 group, "Gesamtleistung" / "Jahresüberschuss" /
                // "Bilanzgewinn" 91 in the income statement, whose items start at level 3, stand at
                // the top. The account-number rule below (main column 1, two sums in a row close the
                // grandparent) does not apply — it made these rows empty items hung under whatever
                // row came last. The export repeats some sums back to back (variants for other legal
                // forms, "Jahresüberschuss" three times): one is kept.
                const previous = flatNodeArray[flatNodeArray.length - 1];
                if (previous?.type === NodeType.SUM && previous.data.itemText === row.itemText) {
                    return;
                }
                node.type = NodeType.SUM;
                const closesGroup = nodesMap.get(levelValue) != null && lastRealLevel > levelValue;
                const parentLevel = closesGroup ? levelValue : levelValue - 1;
                levelValue = parentLevel >= startAt ? parentLevel + 1 : startAt;
                node.data.level = levelValue;
            } else if (levelType === 9) {
                // If starts with a 9, that means it shall not have numbering.
                // And this can be a SUM or an FLI without numbering.
                if (row.mainCol === 1 && row.accountAssignments.length === 0) {
                    node.type = NodeType.SUM;

                    // we need to identify the scope of the SUM
                    // in normal case, the scope is the parent
                    // however if two SUM comes after each other, then it's the grand parent
                    try {
                        let ancestorDistance = 1;
                        if (flatNodeArray.length > 0) {
                            const previeousNode = flatNodeArray[flatNodeArray.length - 1];
                            if (previeousNode.type === NodeType.SUM) {
                                ancestorDistance += 1;
                                levelValue = levelValue - 1;
                                node.data.level = node.data.level - 1;
                            }
                        }
                        node.linked = nodesMap.get(levelValue - ancestorDistance);
                    } catch (error) {
                        debug("try to link where I don't have a parent available");
                    }
                } else {
                    node.type = NodeType.FLI_WITHOUT_NUMBERING;
                }
            } else if (levelType === 8) {
                levelValue = levelValue + 1;
                node.type = NodeType.OF_THAT_ITEM;
            }

            //Ignore UncoverShortfall Items because we add it at the end
            if (
                specialFlis &&
                (id === specialFlis.uncoverShortFallAssetsId || id === specialFlis.uncoverShortFallLiabilitiesId)
            ) {
                return;
            }

            // REV-18627 (I2): über die gesamte Zeile hinweg der Elternknoten, den diese Zeile
            // tatsächlich bekommen hat (Anhang für die OF_THAT-Nachbehandlung unten — dieselbe
            // Referenz, keine zweite Suche).
            let resolvedParent: TreeNode | undefined;
            // REV-22122: a level-0 row is outside the regular outline — but the account-number
            // export puts REAL items there ("Saldo Klasse 9", `9000-S-9140` / `9000-H-9140`), and
            // dropping them left every class-9 account unmapped with no candidate at all. Such a
            // row becomes a top-level item; a row carrying only the `0-x-0` placeholder ("Sonstige
            // Aktiva") or nothing is still ignored. It is never registered as a parent (below): it
            // closes the outline, nothing hangs under it.
            const standaloneLevelZero = row.level === 0 && hasRealAssignment(row.accountAssignments);
            if (standaloneLevelZero) {
                node.type = undefined;
                levelValue = startAt;
            }
            if (row.level === 0 && !standaloneLevelZero) {
                // Level 0 nodes are can always be ignored
                return;
            } else if (ITEMS_TO_INGORE.includes(row.itemText)) {
                // ignore items in this static list
                return;
                // Ignore Uncovershortfall Items for Assets and Liabilities
            } else if (levelValue === startAt && node.type !== NodeType.OF_THAT_ITEM) {
                // Level 1 nodes are considered root nodes (OF_THAT-Zeilen sind nie Wurzel, auch
                // wenn ihr angepasster Level zufällig auf startAt fällt — REV-18627 I2).
                rootNodes.push(node);
            } else if (levelValue > 0 && levelValue < 10) {
                // Find the parent node for non-root nodes (level value > 0 and < 10).
                // REV-18627 (I2): fehlt der exakte Elternschlüssel (die Postenstruktur springt
                // zwischen Gruppen- und Blattebene), wird an den zuletzt gesehenen echten
                // Hierarchieknoten gehängt statt die Zeile stillschweigend zu verwerfen.
                resolvedParent = nodesMap.get(levelValue - 1) ?? lastRealNode;
                if (resolvedParent) {
                    resolvedParent.children.push(node);
                } else {
                    debug('no ancestor available at all, dropping row', row);
                    errors.push(DATEV_ERROR.ORPHAN_NODE);
                }
            } else if (levelType === 0 && levelValue === 0) {
                rootNodes.push(node);
            }
            // Non-leaf nodes are added to the map to maintain hierarchy
            if (levelValue > 0 && levelValue < 10 && levelType === 0 && !standaloneLevelZero) {
                nodesMap.set(levelValue, node);
                lastRealNode = node;
                lastRealLevel = levelValue;
            }

            // Determine the types of the nodes if they don't have values
            // OF-THAT-ITEMS are always a child of a FLI
            if (node.type === NodeType.OF_THAT_ITEM) {
                if (!resolvedParent) {
                    debug('no parent node identified', resolvedParent);
                } else {
                    resolvedParent.type = NodeType.FLI;
                    const grandParentNode = nodesMap.get(levelValue - 2);
                    if (grandParentNode && grandParentNode.type !== NodeType.HIDDEN_GROUP) {
                        grandParentNode.type = NodeType.GROUP;
                    }
                }
            }

            // Determine if it's a GROUP or SUM
            if (node.type === undefined) {
                if (node.data.accountAssignments.length === 0) {
                    let next = undefined;
                    if (data.length >= index + 1) {
                        next = data[index + 1];
                    }
                    // IN case there is a child, then it's a group
                    // REV-22122: in the purpose export the next row may be a sum (9x) or a "davon"
                    // row (8x) — their level number is not a depth, so only a regular row counts
                    // as a child there ("Sonstiger Aufwand" before "Jahresüberschuss" 91 is an item).
                    const nextIsChild =
                        next !== undefined &&
                        next.level > node.data.level &&
                        (!purposeFormat || Math.floor(next.level / 10) === 0);
                    if (nextIsChild) {
                        node.type = NodeType.GROUP;
                        let nextLevelValue = next.level % 10;

                        // IN case there is a child AND the level has more distance than 1, then we need to insert also a hidden group
                        const distance = nextLevelValue - levelValue;
                        if (distance > 1) {
                            let nodeToAddHiddenGroup = node;
                            for (let index = 1; index < distance; index++) {
                                const id = prefix + '_hidden_' + row.itemText;
                                const hiddenGroup: TreeNode = {
                                    id,
                                    data: {
                                        evaluation: row.evaluation,
                                        balanceType: row.balanceType,
                                        level: levelValue + index,
                                        itemText: 'HIDDEN',
                                        mainCol: 0,
                                        preCol: 0,
                                        ofThatCol: 0,
                                        accountAssignments: [],
                                    },
                                    children: [],
                                    type: NodeType.HIDDEN_GROUP,
                                };
                                nodeToAddHiddenGroup.children.push(hiddenGroup);
                                nodeToAddHiddenGroup = hiddenGroup;
                                nodesMap.set(levelValue + index, hiddenGroup);
                            }
                        }
                    } else if (purposeFormat) {
                        // REV-22122: the account-purpose export marks its sums as 9x rows (above). A
                        // regular row without purposes and without children is an ITEM the client
                        // simply does not use ("Genossenschaftliche Rückvergütung", "nicht
                        // beherrschende Anteile") — as a sum it showed a running total under the
                        // item's name, and the income statement looked as if it did not add up.
                        node.type = NodeType.FLI;
                    } else {
                        //In this case it's a sum because it has no child
                        node.type = NodeType.SUM;
                    }
                }
            }

            //if not SUBFLI and OF-THAT Item, then it's probably a FLI
            // FLI is a child of a GROUP or directly assigned in the rootNodes, it's never.
            // FLI can have values in accountAssignment, but it's also possible that they don't have accountAssignment
            if (node.type === undefined && node.data.accountAssignments.length > 0) {
                node.type = NodeType.FLI;
            }

            //GROUPS have no values in accountAssignment and cannot be empty
            //Parent of an FLI is always a group
            //Add all nodes to the node array
            flatNodeArray.push(node);
        });

        if (rootNodes.length == 0) {
            errors.push(DATEV_ERROR.ROOT_NODE_EMPTY);
        }
        return rootNodes;
    }

    /**
     * Build a nested AumicoNode hierarchy from a flat list of AumicoNodes.
     * Nodes at level 1 become root nodes; deeper nodes are attached to the
     * most-recently-seen node at (level - 1).
     */
    private buildAumicoNodeHierarchy(nodes: AumicoNode[]): AumicoNode[] {
        const hierarchy: AumicoNode[] = [];
        const levelMap: { [level: number]: AumicoNode } = {};

        nodes.forEach((node) => {
            const currentLevel = node.level;
            if (currentLevel === 1) {
                hierarchy.push(node);
                levelMap[currentLevel] = node;
            } else {
                const parentNode = levelMap[currentLevel - 1];
                if (parentNode) {
                    parentNode.children.push(node);
                    levelMap[currentLevel] = node;
                } else {
                    console.warn(`No parent found for node: ${node.id} at level ${currentLevel}`);
                }
            }
        });

        return hierarchy;
    }

    /**
     * Convert a single TreeNode (DATEV internal format) into an AumicoNode.
     * Children are converted recursively.
     */
    private treeNodeToAumicoNode(node: TreeNode, evaluation: string): AumicoNode {
        let classification: string;
        let disableNumbering: boolean;

        switch (node.type) {
            case NodeType.GROUP:
                classification = AumicoNodeType.GROUP;
                disableNumbering = false;
                break;
            case NodeType.HIDDEN_GROUP:
                classification = AumicoNodeType.HIDDEN_GROUP;
                disableNumbering = true;
                break;
            case NodeType.FLI:
                classification = AumicoNodeType.FLI;
                disableNumbering = false;
                break;
            case NodeType.FLI_WITHOUT_NUMBERING:
                classification = AumicoNodeType.FLI_WITHOUT_NUMBERING;
                disableNumbering = true;
                break;
            case NodeType.SUBFLI:
                classification = AumicoNodeType.SUB_FLI;
                disableNumbering = true;
                break;
            case NodeType.SUM:
                classification = AumicoNodeType.SUM;
                disableNumbering = false;
                break;
            case NodeType.OF_THAT_ITEM:
                classification = AumicoNodeType.OF_THAT_ITEM;
                disableNumbering = true;
                break;
            default:
                classification = AumicoNodeType.UNKNOWN;
                disableNumbering = false;
                break;
        }

        return {
            evaluation,
            level: node.data.level % 10 || node.data.level,
            classification,
            itemText: node.data.itemText,
            accountAssignments: node.data.accountAssignments,
            id: node.id,
            disableNumbering,
            children: node.children.map((child) => this.treeNodeToAumicoNode(child, evaluation)),
        };
    }

    /**
     * Convert a list of root TreeNodes into AumicoNode[].
     */
    private treeNodesToAumicoNodes(nodes: TreeNode[], evaluation: string): AumicoNode[] {
        return nodes.map((node) => this.treeNodeToAumicoNode(node, evaluation));
    }

    /**
     * Extract the three taxonomy sections (Bilanz-Aktiva, Bilanz-Passiva, GuV)
     * as AumicoNode[] trees directly from the raw spreadsheet.
     *
     * Handles both DATEV-format and aumico-format files using the same logic
     * as createTaxonomy / createTaxonomyFromAumicoFile.
     */
    public extractAumicoNodeSections(spreadSheetData: ISpreadSheet): {
        'Bilanz-Aktiva': AumicoNode[];
        'Bilanz-Passiva': AumicoNode[];
        GuV: AumicoNode[];
    } {
        const empty = { 'Bilanz-Aktiva': [], 'Bilanz-Passiva': [], GuV: [] };

        const taxonomySheet = spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_STRUCTURE_SHEET,
        );
        if (!taxonomySheet) return empty;

        const format = this.checkTaxonomyFormat(taxonomySheet);

        if (format === 'aumico') {
            const allNodes: AumicoNode[] = taxonomySheet.rows
                .map((row: ISheetRow) => {
                    const level = Number(row.cells[1]);
                    if (isNaN(level)) return null;
                    const disableNumbering = Boolean(row.cells[6]);
                    return {
                        evaluation: row.cells[0],
                        level,
                        classification: row.cells[2],
                        itemText: row.cells[3],
                        accountAssignments: row.cells[4].split(',').map((item) => item.trim()),
                        id: row.cells[5],
                        disableNumbering,
                        children: [],
                    } as AumicoNode;
                })
                .filter((n): n is AumicoNode => n !== null);

            return {
                'Bilanz-Aktiva': this.buildAumicoNodeHierarchy(
                    allNodes.filter((n) => n.evaluation === AUMICO_IMPORT.ASSETS),
                ),
                'Bilanz-Passiva': this.buildAumicoNodeHierarchy(
                    allNodes.filter((n) => n.evaluation === AUMICO_IMPORT.LIABILITIES),
                ),
                GuV: this.buildAumicoNodeHierarchy(allNodes.filter((n) => n.evaluation === AUMICO_IMPORT.INCOME)),
            };
        }

        if (format === 'DATEV') {
            const processedRows = taxonomySheet.rows
                .map((row: ISheetRow) => {
                    const level = Number(row.cells[2]);
                    if (isNaN(level)) return null;
                    const mainCol = Number(row.cells[4]);
                    const preCol = Number(row.cells[5]);
                    const ofThatCol = Number(row.cells[6]);
                    const result: DatevRows = {
                        evaluation: row.cells[0],
                        balanceType: row.cells[1],
                        level,
                        itemText: row.cells[3],
                        mainCol,
                        preCol,
                        ofThatCol,
                        accountAssignments: [],
                    };
                    for (let i = 7; i < row.cells.length; i++) {
                        if (row.cells[i] !== '') result.accountAssignments.push(row.cells[i]);
                        else break;
                    }
                    return result;
                })
                .filter((r): r is DatevRows => r !== null);

            const errors: DATEV_ERROR[] = [];
            const balanceRows = processedRows.filter(
                (r) => r.evaluation === MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
            );

            return {
                'Bilanz-Aktiva': this.treeNodesToAumicoNodes(
                    this.createTree(
                        balanceRows.filter((r) => r.balanceType === BALANCE_TYPE_ASSETS),
                        undefined,
                        PREFIX_BALANCE_ASSETS_ITEM,
                        errors,
                    ),
                    MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
                ),
                'Bilanz-Passiva': this.treeNodesToAumicoNodes(
                    this.createTree(
                        balanceRows.filter((r) => r.balanceType === BALANCE_TYPE_LIABILITIES),
                        undefined,
                        PREFIX_BALANCE_LIABILITIES_ITEM,
                        errors,
                    ),
                    MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
                ),
                GuV: this.treeNodesToAumicoNodes(
                    this.createTree(
                        processedRows.filter((r) => r.evaluation === MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET),
                        undefined,
                        PREFIX_INCOME_ITEM,
                        errors,
                    ),
                    MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
                ),
            };
        }

        return empty;
    }

    /**
     * Extracts the SpecialFLIs configuration from the template-info sheet of a spreadsheet.
     * Returns null if the spreadsheet does not contain the required info sheet.
     */
    public extractSpecialFlisFromSpreadSheet(spreadSheetData: ISpreadSheet): SpecialFLIs | null {
        const taxonmyInformation = spreadSheetData.sheets.find(
            (sheet) => sheet.name === MASTER_DATA_DEFAULT_SHEET_NAMES.TEMPLATE_INFO_SHEET,
        );
        if (!taxonmyInformation) return null;

        const emptyText = '<keine Angabe';
        const annualSurplusFliName = taxonmyInformation.rows[3]?.cells[1];
        const profitFliName = taxonmyInformation.rows[4]?.cells[1];
        const cashFliName = taxonmyInformation.rows[5]?.cells[1];
        const uncoverShortFallAssets =
            taxonmyInformation.rows[0]?.cells[1] === emptyText ? undefined : taxonmyInformation.rows[0]?.cells[1];
        const uncoverShortFallLiabilities =
            taxonmyInformation.rows[1]?.cells[1] === emptyText ? undefined : taxonmyInformation.rows[1]?.cells[1];
        const equityGroupName =
            taxonmyInformation.rows[2]?.cells[1] === emptyText ? undefined : taxonmyInformation.rows[2]?.cells[1];

        return {
            equityGroupName,
            annualSurplus: annualSurplusFliName,
            profitOfTheYear: profitFliName,
            uncoverShortFallAssets,
            uncoverShortFallLiabilities,
            cashFlow: cashFliName,
        };
    }

    public convertAumicoSectionsToTaxonomy(
        sections: { 'Bilanz-Aktiva': AumicoNode[]; 'Bilanz-Passiva': AumicoNode[]; GuV: AumicoNode[] },
        specialFlis: SpecialFLIs,
    ): TaxonomyDefinitions {
        const assetsHierarchy = sections['Bilanz-Aktiva'];
        const liabilitiesHierarchy = sections['Bilanz-Passiva'];
        const incomeHierarchy = sections['GuV'];

        const assetsGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.ASSETS,
            title: BALANCE_TYPE_ASSETS,
            totalName: BALANCE_TYPE_ASSETS,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };
        const convertedAssets = this.convertNodesHierarchyArrayToIAccountMappingTemplateGroup(
            assetsHierarchy,
            assetsGroup,
            FinancialLineItemType.ASSETS,
            specialFlis,
            true,
        );

        const liabGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.LIABILITIES,
            title: BALANCE_TYPE_LIABILITIES,
            totalName: BALANCE_TYPE_LIABILITIES,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };
        const convertedLiabilities = this.convertNodesHierarchyArrayToIAccountMappingTemplateGroup(
            liabilitiesHierarchy,
            liabGroup,
            FinancialLineItemType.LIABILITIES,
            specialFlis,
            true,
        );

        const incomeGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.PROFIT_OF_THE_YEAR,
            title: MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            totalName: MASTER_DATA_DEFAULT_SHEET_NAMES.INCOME_SHEET,
            hideTotal: false,
            groups: [],
            items: [],
            sortedItems: [],
        };
        const convertedIncome = this.convertNodesHierarchyArrayToIAccountMappingTemplateGroup(
            incomeHierarchy,
            incomeGroup,
            FinancialLineItemType.INCOME,
            specialFlis,
            false,
        );

        const balanceGroup: IAccountMappingTemplateGroup = {
            name: DefaultGroupsNames.BALANCE_SHEET,
            title: MASTER_DATA_DEFAULT_SHEET_NAMES.BALANCE_SHEET,
            totalName: 'Bilanzsumme',
            hideTotal: false,
            groups: [convertedAssets, convertedLiabilities],
            items: [],
            sortedItems: [convertedAssets, convertedLiabilities],
        };

        const balanceGroupWithSortedItemsNameType = SortingItemsHelper.mapGroupSortedItemsToItemNameType(balanceGroup);
        const incomeGroupWithSortedItemsNameType =
            SortingItemsHelper.mapGroupSortedItemsToItemNameType(convertedIncome);

        const accountMapping1 = getAccountAssignmentsFromAumicoNode(assetsHierarchy);
        const accountMapping2 = getAccountAssignmentsFromAumicoNode(liabilitiesHierarchy);
        const accountAssignmentBalanceSheet = mergeAccountMappings(accountMapping1, accountMapping2);
        const accountAssignmentIncomeStatement = getAccountAssignmentsFromAumicoNode(incomeHierarchy);

        const accountMappingBS: IAccountMappingMap = mapToIAccountMappingMap(accountAssignmentBalanceSheet);
        const accountMappingIS: IAccountMappingMap = mapToIAccountMappingMap(accountAssignmentIncomeStatement);

        const groupsOfAssets = this.getAllGroupsFromAumicoNode(assetsHierarchy);
        const groupsOfLia = this.getAllGroupsFromAumicoNode(liabilitiesHierarchy);
        const allgroups = ([] as { name: string; id: string }[])
            .concat(...groupsOfAssets, ...groupsOfLia)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((group) => group.name.trim() !== '');

        const flisAssets = this.getAllFlisFromAumicoNode(assetsHierarchy, specialFlis)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');
        const flisLia = this.getAllFlisFromAumicoNode(liabilitiesHierarchy, specialFlis)
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');
        const allFlis = ([] as { name: string; id: string }[])
            .concat(...[flisAssets, flisLia])
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((fli) => fli.name.trim() !== '');

        // REV-22122: exact name first, then the narrower readings of a purpose-format `Posteninfo`
        // entry ("Bilanzgewinn / Bilanzverlust" → "Bilanzgewinn") — see `specialItemNameCandidates`.
        function findIdByName(name: string, items: { name: string; id: string }[]): string | undefined {
            for (const candidate of specialItemNameCandidates(name)) {
                const item = items.find((node) => node.name.trim() === candidate);
                if (item) return item.id;
            }
            return undefined;
        }

        specialFlis.equityGroupNameId = findIdByName(specialFlis.equityGroupName || '', allgroups);
        specialFlis.annualSurplusId = findIdByName(specialFlis.annualSurplus || '', allFlis);
        specialFlis.profitOfTheYearId = findIdByName(specialFlis.profitOfTheYear || '', allFlis);
        specialFlis.uncoverShortFallAssetsId = findIdByName(specialFlis.uncoverShortFallAssets || '', flisAssets);
        specialFlis.uncoverShortFallLiabilitiesId = findIdByName(
            specialFlis.uncoverShortFallLiabilities || '',
            flisLia,
        );
        specialFlis.cashFlowId = findIdByName(specialFlis.cashFlow || '', allFlis);

        const balanceTaxonomy: IAccountMappingTemplate = {
            group: balanceGroupWithSortedItemsNameType,
            name: 'Balance Sheet',
            type: TemplateType.BALANCE,
            accountBasis: '',
            legalForm: '',
            equityGroupName: specialFlis.equityGroupNameId,
            annualSurplusFli: specialFlis.annualSurplusId,
            profitOfTheYear: specialFlis.profitOfTheYearId,
            uncoveredShortfallAssetsFli: specialFlis.uncoverShortFallAssetsId,
            uncoveredShortfallLiabilitiesFli: specialFlis.uncoverShortFallLiabilitiesId,
            accountAssignments: accountMappingBS,
            flis: allFlis,
            flisAssets: flisAssets,
            flisLia: flisLia,
            groups: allgroups,
        };

        const incomeTaxonomy: IAccountMappingTemplate = {
            group: incomeGroupWithSortedItemsNameType,
            name: 'Income Sheet',
            type: TemplateType.INCOME,
            accountBasis: '',
            legalForm: '',
            equityGroupName: specialFlis.equityGroupNameId,
            annualSurplusFli: specialFlis.annualSurplusId,
            profitOfTheYear: specialFlis.profitOfTheYearId,
            uncoveredShortfallAssetsFli: specialFlis.uncoverShortFallAssetsId,
            uncoveredShortfallLiabilitiesFli: specialFlis.uncoverShortFallLiabilitiesId,
            accountAssignments: accountMappingIS,
        };

        return {
            rawBalanceTemplate: balanceTaxonomy,
            rawIncomeTemplate: incomeTaxonomy,
        };
    }

    checkTaxonomyFormat(taxonomySheet: ISheet): string {
        // Extract the headers from the first row of the sheet
        const headers = taxonomySheet.rows[0].cells;

        // Define expected headers for DATEV format
        const datevHeaders = [
            'Auswertung',
            'Saldoart',
            'Ebene',
            'Postentext',
            'Hauptspalte',
            'Vorspalte',
            'Davonspalte',
            'Kontenzuordnung',
        ];

        // Define expected headers for Aumico format
        const aumicoHeaders = [
            'Group',
            'Level',
            'Classification',
            'Name',
            'AccountAssignments',
            'ID',
            'disableNumbering',
        ];

        // REV-18627 (B11): dritter Fall — dieselben Kopfspalten, letzte Spalte "Kontenzweckzuordnung"
        // statt "Kontenzuordnung" (Postenstruktur der Zweck-Dateien).
        const purposeHeaders = [...datevHeaders.slice(0, 7), 'Kontenzweckzuordnung'];

        // Check if headers match the DATEV format
        const isDatevFormat =
            headers.length === datevHeaders.length && datevHeaders.every((header, index) => headers[index] === header);

        const isPurposeFormat =
            headers.length === purposeHeaders.length &&
            purposeHeaders.every((header, index) => headers[index] === header);

        // Check if headers match the Aumico format
        const isAumicoFormat =
            headers.length === aumicoHeaders.length &&
            aumicoHeaders.every((header, index) => headers[index] === header);

        // Determine the format
        if (isDatevFormat) {
            return 'DATEV';
        } else if (isPurposeFormat) {
            return DATEV_TAXONOMY_FORMAT_PURPOSE;
        } else if (isAumicoFormat) {
            return 'aumico';
        } else {
            return 'Unknown';
        }
    }

    createTaxonomyFromAumicoFile(
        taxonomySheet: ISheet,
        specialFlis: SpecialFLIs,
        errors: DATEV_ERROR[],
    ): TaxonomyDefinitionsWithErrorInformation {
        const allNodes: AumicoNode[] = taxonomySheet.rows
            .map((row: ISheetRow) => {
                // Check if 'level' can be converted to a number
                const level = Number(row.cells[1]);
                const disableNumbering = Boolean(row.cells[6]);

                if (!isNaN(level)) {
                    // If 'level' is a valid number
                    const response: AumicoNode = {
                        evaluation: row.cells[0],
                        level,
                        classification: row.cells[2],
                        itemText: row.cells[3],
                        accountAssignments: row.cells[4].split(',').map((item) => item.trim()),
                        id: row.cells[5],
                        disableNumbering,
                        children: [],
                    };

                    return response;
                } else {
                    return null;
                }
            })
            .filter((response) => response !== null); // Filter out null responses

        //Split nodes in different groups for assets, liabilities and income
        const assetsHierarchy = this.buildAumicoNodeHierarchy(
            allNodes.filter((row) => row.evaluation === AUMICO_IMPORT.ASSETS),
        );

        const liabilitiesAndEquityHierarchy = this.buildAumicoNodeHierarchy(
            allNodes.filter((row) => row.evaluation === AUMICO_IMPORT.LIABILITIES),
        );
        const incomeHierarchy = this.buildAumicoNodeHierarchy(
            allNodes.filter((row) => row.evaluation === AUMICO_IMPORT.INCOME),
        );

        const result = this.convertAumicoSectionsToTaxonomy(
            {
                'Bilanz-Aktiva': assetsHierarchy,
                'Bilanz-Passiva': liabilitiesAndEquityHierarchy,
                GuV: incomeHierarchy,
            },
            specialFlis,
        );
        return {
            taxonomyDefinitions: result,
            errors: [],
        };
    }

    public convertNodesHierarchyArrayToIAccountMappingTemplateGroup(
        rootNodes: AumicoNode[],
        rootGroup: IAccountMappingTemplateGroup,
        fliType: FinancialLineItemType,
        specialFlis: SpecialFLIs,
        disableNumberingForSumsByDefault: boolean,
    ): IAccountMappingTemplateGroup {
        const convertNode = (
            node: AumicoNode,
            parentGroup?: IAccountMappingTemplateGroup,
            parentItem?: AccountMappingTemplateGroupItem,
        ): void => {
            const disableNumbering = node.disableNumbering;
            if (node.classification === AumicoNodeType.GROUP) {
                // Create Group
                const group: IAccountMappingTemplateGroup = {
                    name: node.id,
                    title: node.itemText,
                    totalName: node.itemText,
                    hideTotal: true,
                    isHiddenGroup: false,
                    groups: [],
                    items: [],
                    sortedItems: [],
                    disableNumbering,
                };
                if (parentGroup != null) {
                    const sortedItems = parentGroup.sortedItems as SortedMappingItem[];
                    parentGroup.groups.push(group);
                    sortedItems.push(group);
                    parentGroup.sortedItems = sortedItems;
                }
                // Convert children nodes
                node.children.forEach((childNode) => convertNode(childNode, group));
            } else if (node.classification === AumicoNodeType.HIDDEN_GROUP) {
                // Create Group
                const group: IAccountMappingTemplateGroup = {
                    name: node.id,
                    title: 'Einrückung ' + node.itemText,
                    totalName: node.itemText,
                    hideTotal: true,
                    isHiddenGroup: true,
                    groups: [],
                    items: [],
                    sortedItems: [],
                    disableNumbering,
                };
                if (parentGroup != null) {
                    const sortedItems = parentGroup.sortedItems as SortedMappingItem[];
                    parentGroup.groups.push(group);
                    sortedItems.push(group);
                    parentGroup.sortedItems = sortedItems;
                    // Convert children nodes
                    node.children.forEach((childNode) => convertNode(childNode, group));
                }
            } else if (
                node.classification === AumicoNodeType.FLI ||
                node.classification === AumicoNodeType.FLI_WITHOUT_NUMBERING ||
                node.classification === AumicoNodeType.X_FLI
            ) {
                // Create Item
                const item: IAccountMappingTemplateGroupItemFLI = {
                    name: node.id,
                    caption: node.itemText,
                    type: ItemType.FLI,
                    fliType,
                    accounts: [],
                    reclassesAccounts: [],
                    disableNumbering: disableNumbering,
                };
                parentGroup?.items.push(item);
                parentGroup?.sortedItems.push(item);
                // Convert children nodes
                node.children.forEach((childNode) => convertNode(childNode, undefined, item));
            } else if (node.classification === AumicoNodeType.OF_THAT_ITEM) {
                // Create Item
                const ofthatitem: IAccountMappingSubFLI = {
                    type: ItemType.OF_THAT_ITEM,
                    name: node.id,
                    caption: node.itemText,
                    accounts: [],
                    reclassesAccounts: [],
                };
                // Add item creation logic here, pushing to parentGroup.items
                (parentItem as IAccountMappingTemplateGroupItemFLI).accounts.push(ofthatitem);
            } else if (node.classification === AumicoNodeType.SUB_FLI) {
                // Create Item
                const subfli: IAccountMappingSubFLI = {
                    type: ItemType.SUB_FLI,
                    name: node.id,
                    caption: node.itemText,
                    accounts: [],
                    reclassesAccounts: [],
                };
                // Add item creation logic here, pushing to parentGroup.items
                (parentItem as IAccountMappingTemplateGroupItemFLI).accounts.push(subfli);
            } else if (node.classification === AumicoNodeType.SUM) {
                //profitOfTheYear1 must always be an FLI
                //TODO verify if this works, I assume the ID will not be correct
                if (node.id === specialFlis.profitOfTheYearId || node.id === specialFlis.annualSurplusId) {
                    const item: IAccountMappingTemplateGroupItemFLI = {
                        name: node.id,
                        caption: node.itemText,
                        type: ItemType.FLI,
                        fliType,
                        accounts: [],
                        reclassesAccounts: [],
                        disableNumbering,
                    };

                    parentGroup?.items.push(item);
                    parentGroup?.sortedItems.push(item);
                } else {
                    // Create Sum
                    const scope: IItemNameType = {
                        name: parentGroup.name,
                        type: ItemType.GROUP,
                    };
                    const item: AccountMappingTemplateGroupItem = {
                        name: node.id,
                        caption: node.itemText,
                        fliType,
                        type: ItemType.SUB_TOTAL,
                        scope,
                        disableNumbering: disableNumbering,
                    };
                    parentGroup?.items.push(item);
                    parentGroup?.sortedItems.push(item);

                    // Convert children nodes
                    node.children.forEach((childNode) => convertNode(childNode, undefined, item));
                }

                // Add sum creation logic here
            } else {
                throw new Error(`Method not implemented for classification. ${node.classification}`);
            }
            // Handle other types if needed
        };

        // Convert each root node
        rootNodes.forEach((node) => convertNode(node, rootGroup));

        return rootGroup;
    }
}
function getAccountAssignments(tree: TreeNode[]): Map<string, string[]> {
    const accountAssignement: Map<string, string[]> = new Map<string, string[]>();

    function processNode(node: TreeNode) {
        // Add account assignments for the current node
        if (node.data.accountAssignments.length > 0) {
            //const accounts = node.data.accountAssignments.flatMap(expandRange);
            accountAssignement.set(node.id, node.data.accountAssignments);
        }

        // Recursively process each child node
        node.children.forEach((childNode) => processNode(childNode));
    }

    // Start the recursive process for each root node
    tree.forEach((rootNode) => processNode(rootNode));

    return accountAssignement;
}
function getAccountAssignmentsFromAumicoNode(tree: AumicoNode[]): Map<string, string[]> {
    const accountAssignement: Map<string, string[]> = new Map<string, string[]>();

    function processNode(node: AumicoNode) {
        // Add account assignments for the current node
        if (node.accountAssignments.length > 0) {
            //const accounts = node.data.accountAssignments.flatMap(expandRange);
            accountAssignement.set(node.id, node.accountAssignments);
        }

        // Recursively process each child node
        node.children.forEach((childNode) => processNode(childNode));
    }

    // Start the recursive process for each root node
    tree.forEach((rootNode) => processNode(rootNode));

    return accountAssignement;
}

function mergeAccountMappings(...maps: Map<string, string[]>[]): Map<string, string[]> {
    const mergedMap = new Map<string, string[]>();

    maps.forEach((map) => {
        map.forEach((value, key) => {
            if (mergedMap.has(key)) {
                // If the key already exists, merge the arrays
                const existing = mergedMap.get(key);
                mergedMap.set(key, [...existing!, ...value]);
            } else {
                // If the key doesn't exist, add it to the map
                mergedMap.set(key, value);
            }
        });
    });

    return mergedMap;
}
function findNodeById(tree: TreeNode[], id: string): TreeNode | null {
    function searchNode(node: TreeNode): TreeNode | null {
        // Check if the current node's ID matches the target ID
        if (node.id === id) {
            return node;
        }

        // Recursively search in each child node
        for (const childNode of node.children) {
            const result = searchNode(childNode);
            if (result) return result; // Node found in a subtree
        }

        return null; // Node not found in this subtree
    }

    // Start the recursive search for each root node
    for (const rootNode of tree) {
        const result = searchNode(rootNode);
        if (result) return result; // Node found in one of the trees
    }

    return null; // Node not found in any tree
}

function expandRange(range: string): string[] {
    const delimiterPattern = /-S-|-D-|-H-/;
    const parts = range.split(delimiterPattern);
    if (parts.length !== 2) {
        console.error('Invalid range format:', range);
        return [];
    }

    const [start, end] = parts;
    const startNum = parseInt(start);
    const endNum = parseInt(end);

    if (startNum === 0 && endNum === 0) {
        return []; // Ignore ranges where start and end are 0
    }

    const expandedRange: string[] = [];

    if (!isNaN(startNum) && !isNaN(endNum)) {
        for (let i = startNum; i <= endNum; i++) {
            expandedRange.push(i.toString());
        }
    } else {
        console.error('Invalid start or end number:', start, end);
    }

    return expandedRange;
}

function mapToIAccountMappingMap(map: Map<string, string[]>): IAccountMappingMap {
    return Object.fromEntries(map) as IAccountMappingMap;
}
